import { Node } from '@tiptap/core'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Table } from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TextAlign from '@tiptap/extension-text-align'
import { TextStyle } from '@tiptap/extension-text-style'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import Underline from '@tiptap/extension-underline'
import { EditorContent, useEditor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import StarterKit from '@tiptap/starter-kit'
import DOMPurify from 'dompurify'
import { Camera, Eye, ImagePlus, ListTodo, Paperclip, Redo2, Undo2 } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, absoluteApiV1Path, canonicalStoredFileIdForApiPath, resolveApiAssetUrl } from '../lib/api'
import { useToast } from '../ui/ToastProvider'

const MAX_IMAGE_BYTES = 5 * 1024 * 1024
const MAX_FILE_BYTES = 10 * 1024 * 1024
const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024
const HIGHLIGHT_COLOR = '#fef08a'
const MEMO_BLUE = '#2563eb'

function normalizeMemoImageAlign(value) {
  const raw = String(value || '').trim()
  return ['left', 'center', 'right'].includes(raw) ? raw : ''
}

function memoImageAlignStyle(value) {
  const align = normalizeMemoImageAlign(value)
  if (align === 'center') return 'display: block; margin-left: auto; margin-right: auto;'
  if (align === 'right') return 'display: block; margin-left: auto; margin-right: 0;'
  if (align === 'left') return 'display: block; margin-left: 0; margin-right: auto;'
  return ''
}
function numberLimit(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}
const MEMO_ALLOWED_DATA_ATTRIBUTES = new Set(['data-align', 'data-customer-memo-file', 'data-file-id', 'data-file-name', 'data-file-size', 'data-content-type', 'data-type', 'data-checked'])

function sanitizeMemoHtml(html) {
  const sanitized = DOMPurify.sanitize(html || '', {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['style', 'class', 'target', 'rel', 'colspan', 'rowspan', 'colwidth', 'width', 'height', ...MEMO_ALLOWED_DATA_ATTRIBUTES],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'svg', 'math'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/|#)/i,
  })
  if (typeof document === 'undefined') return sanitized
  const template = document.createElement('template')
  template.innerHTML = sanitized
  template.content.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach(({ name }) => {
      if (name.startsWith('data-') && !MEMO_ALLOWED_DATA_ATTRIBUTES.has(name)) {
        element.removeAttribute(name)
      }
    })
  })
  return template.innerHTML
}

function encodeMemoHtmlForStorage(html) {
  const value = String(html || '')
  if (!value) return ''
  try {
    if (typeof TextEncoder !== 'undefined') {
      const bytes = new TextEncoder().encode(value)
      let binary = ''
      bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
      return btoa(binary)
    }
    return btoa(unescape(encodeURIComponent(value)))
  } catch {
    return ''
  }
}

function decodeMemoHtmlFromStorage(encoded) {
  const value = String(encoded || '')
  if (!value) return ''
  try {
    const binary = atob(value)
    if (typeof TextDecoder !== 'undefined') {
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
      return new TextDecoder().decode(bytes)
    }
    return decodeURIComponent(escape(binary))
  } catch {
    return ''
  }
}
function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function memoPlainTextToHtml(plainText) {
  const text = String(plainText || '')
  if (!text.trim()) return ''
  return text
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

export function memoContentToHtml(content, plainText = '') {
  const encodedHtml = content && typeof content === 'object'
    ? (content.htmlBase64 || content.htmlB64 || content.contentHtmlBase64 || content.contentHtmlB64)
    : ''
  if (encodedHtml) {
    return sanitizeMemoHtml(decodeMemoHtmlFromStorage(encodedHtml))
  }
  if (content && typeof content === 'object' && typeof content.html === 'string') {
    return sanitizeMemoHtml(content.html)
  }
  if (content && typeof content === 'object' && typeof content.contentHtml === 'string') {
    return sanitizeMemoHtml(content.contentHtml)
  }
  return memoPlainTextToHtml(plainText)
}

export function CustomerMemoRichContent({ content, plainText = '', className = '' }) {
  const rootRef = useRef(null)
  const html = useMemo(() => memoContentToHtml(content, plainText), [content, plainText])

  useEffect(() => {
    const root = rootRef.current
    if (!root || typeof URL === 'undefined') return undefined
    let cancelled = false
    const objectUrls = []
    root.querySelectorAll('img').forEach((image) => {
      const rawFileId = image.getAttribute('data-file-id')
        || storedFileIdFromMemoImageSrc(image.getAttribute('src'))
      const fileId = canonicalStoredFileIdForApiPath(rawFileId)
      if (!fileId) return
      image.removeAttribute('src')
      image.setAttribute('data-preview-state', 'loading')
      api.get(`/files/${encodeURIComponent(fileId)}/preview`, {
        responseType: 'blob',
        __silent: true,
      }).then((res) => {
        const contentType = res.headers?.['content-type'] || 'image/png'
        const objectUrl = URL.createObjectURL(new Blob([res.data], { type: contentType }))
        if (cancelled) {
          URL.revokeObjectURL(objectUrl)
          return
        }
        objectUrls.push(objectUrl)
        image.setAttribute('src', objectUrl)
        image.setAttribute('data-preview-state', 'ready')
      }).catch(() => {
        if (cancelled) return
        image.setAttribute('data-preview-state', 'error')
        if (!image.getAttribute('alt')) image.setAttribute('alt', '이미지를 불러오지 못했습니다.')
      })
    })
    return () => {
      cancelled = true
      objectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch {}
      })
    }
  }, [html])

  return (
    <div
      ref={rootRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function ToolbarBtn({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={`rounded border px-2 py-1 text-xs font-semibold ${
        active
          ? 'border-sky-300 bg-sky-100 text-sky-900'
          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      {children}
    </button>
  )
}

function unwrapUpload(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function makeAttachment(file, body, index) {
  const fileId = body?.fileId || body?.id
  return {
    fileId,
    kind: String(file?.type || body?.contentType || '').toLowerCase().startsWith('image/') ? 'IMAGE' : 'FILE',
    caption: '',
    sortOrder: index,
    originalName: file?.name || body?.originalName || fileId,
    contentType: file?.type || body?.contentType || '',
    size: file?.size ?? body?.size ?? null,
  }
}

function attachmentName(att) {
  return att?.originalName || att?.filename || att?.fileName || att?.fileId || '첨부 파일'
}

function attachmentDownloadHref(fileId) {
  const id = canonicalStoredFileIdForApiPath(fileId)
  return id ? absoluteApiV1Path(`files/${encodeURIComponent(id)}/download`) : undefined
}

function memoFileRouteFromUrl(url) {
  const raw = String(url || '').trim()
  if (!raw || /^(data:|blob:|mailto:|tel:)/i.test(raw)) return null
  let pathname = raw
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://placeholder.local')
    pathname = parsed.pathname
  } catch {}
  const match = String(pathname || '').match(/(?:^|\/)v?1?\/?files\/([^/?#]+)\/(preview|download)\/?$/i)
  if (!match) return null
  let fileId = match[1]
  try { fileId = decodeURIComponent(fileId) } catch {}
  fileId = canonicalStoredFileIdForApiPath(fileId)
  return fileId ? { fileId, action: String(match[2] || 'download').toLowerCase() } : null
}

function filenameFromContentDisposition(header, fallback = 'download') {
  const cd = String(header || '')
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i)
  if (star?.[1]) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')) } catch { return star[1].trim().replace(/^"|"$/g, '') }
  }
  const quoted = cd.match(/filename="([^"]+)"/i)
  if (quoted?.[1]) return quoted[1]
  const plain = cd.match(/filename=([^;]+)/i)
  if (plain?.[1]) return plain[1].trim().replace(/^"|"$/g, '')
  return fallback || 'download'
}

function postToSgezAppBridge(payload) {
  if (typeof window === 'undefined') return false
  const bridge = window.SgezAppBridge
  if (!bridge || typeof bridge.postMessage !== 'function') return false
  try {
    bridge.postMessage(JSON.stringify(payload))
    return true
  } catch {
    return false
  }
}

async function openAuthenticatedMemoFile({ fileId, action = 'download', fallbackName = 'download', show }) {
  const id = canonicalStoredFileIdForApiPath(fileId)
  if (!id || typeof window === 'undefined' || typeof URL === 'undefined') return
  const safeAction = action === 'preview' ? 'preview' : 'download'
  if (postToSgezAppBridge({
    type: 'openCustomerMemoFile',
    fileId: id,
    action: safeAction,
    fileName: fallbackName || id,
  })) return
  try {
    const res = await api.get(`/files/${encodeURIComponent(id)}/${safeAction}`, {
      responseType: 'blob',
      __silent: true,
    })
    const contentType = res.headers?.['content-type'] || 'application/octet-stream'
    const blob = (typeof Blob !== 'undefined' && res.data instanceof Blob) ? res.data : new Blob([res.data], { type: contentType })
    const url = URL.createObjectURL(blob)
    if (safeAction === 'preview') {
      const opened = window.open(url, '_blank', 'noopener,noreferrer')
      if (!opened) {
        const a = document.createElement('a')
        a.href = url
        a.target = '_blank'
        a.rel = 'noreferrer'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      window.setTimeout(() => { try { URL.revokeObjectURL(url) } catch {} }, 60000)
      return
    }
    const a = document.createElement('a')
    a.href = url
    a.download = filenameFromContentDisposition(res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'], fallbackName || id)
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.setTimeout(() => { try { URL.revokeObjectURL(url) } catch {} }, 1000)
  } catch (err) {
    show?.(err?.response?.data?.message || '파일을 열 수 없습니다.', 'error')
  }
}

function attachmentIsImage(att) {
  const kind = String(att?.kind || att?.attachmentKind || '').toUpperCase()
  const ct = String(att?.contentType || '').toLowerCase()
  return kind === 'IMAGE' || ct.startsWith('image/')
}
function storedFileIdFromMemoImageSrc(src) {
  const s = String(src || '').trim()
  if (!s || /^(data:|blob:)/i.test(s)) return ''
  const parsePath = (path) => {
    const match = String(path || '').match(/\/files\/([^/?#]+)\/(?:preview|download)(?:[?#].*)?$/)
    if (!match) return ''
    try {
      return decodeURIComponent(match[1])
    } catch {
      return match[1]
    }
  }
  if (typeof window !== 'undefined') {
    try {
      const url = new URL(s, window.location.origin)
      const fromPath = parsePath(url.pathname)
      if (fromPath) return fromPath
    } catch {}
  }
  const fromRawPath = parsePath(s)
  if (fromRawPath) return fromRawPath
  const canonical = canonicalStoredFileIdForApiPath(s)
  return canonical && canonical.includes('__') ? canonical : ''
}

function sameStoredFileId(left, right) {
  const a = canonicalStoredFileIdForApiPath(left)
  const b = canonicalStoredFileIdForApiPath(right)
  return Boolean(a && b && a === b)
}

function removeMemoImagesForFileId(editor, fileId) {
  if (!editor || !fileId) return
  const removals = []
  editor.state.doc.descendants((node, pos) => {
    const typeName = node.type?.name
    let nodeFileId = ''
    if (typeName === 'image') nodeFileId = storedFileIdFromMemoImageSrc(node.attrs?.src)
    else if (typeName === 'customerMemoFile') nodeFileId = node.attrs?.fileId || ''
    else if (typeName === 'text') {
      const linkMark = Array.from(node.marks || []).find((mark) => mark.type?.name === 'link')
      nodeFileId = memoFileRouteFromUrl(linkMark?.attrs?.href)?.fileId || ''
    } else return
    if (sameStoredFileId(nodeFileId, fileId)) {
      removals.push({ pos, size: node.nodeSize })
    }
  })
  if (removals.length === 0) return
  let tr = editor.state.tr
  removals
    .sort((a, b) => b.pos - a.pos)
    .forEach(({ pos, size }) => {
      tr = tr.delete(pos, pos + size)
    })
  editor.view.dispatch(tr.scrollIntoView())
}

const CustomerMemoImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute('width') || element.style.width || null,
        renderHTML: (attributes) => {
          const raw = String(attributes.width || '').trim()
          const alignStyle = memoImageAlignStyle(attributes.align)
          if (!raw) {
            return alignStyle ? { style: `${alignStyle} max-width: 100%; height: auto;` } : {}
          }
          const value = raw.endsWith('%') ? raw : String(Math.max(120, Math.min(1200, Number.parseInt(raw, 10) || 0)))
          const cssWidth = value.endsWith('%') ? value : `${value}px`
          return { width: value, style: `width: ${cssWidth}; max-width: 100%; height: auto; ${alignStyle}`.trim() }
        },
      },
      align: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-align') || element.getAttribute('align') || null,
        renderHTML: (attributes) => {
          const align = normalizeMemoImageAlign(attributes.align)
          return align ? { 'data-align': align } : {}
        },
      },
    }
  },
  addNodeView() {
    return ({ node, editor, getPos }) => {
      let currentNode = node
      const wrapper = document.createElement('span')
      wrapper.className = 'customer-memo-image-resizer'
      wrapper.setAttribute('contenteditable', 'false')
      const img = document.createElement('img')
      const resizeHandle = document.createElement('span')
      resizeHandle.className = 'customer-memo-image-resize-handle'
      resizeHandle.setAttribute('aria-hidden', 'true')
      resizeHandle.setAttribute('contenteditable', 'false')
      wrapper.appendChild(img)
      wrapper.appendChild(resizeHandle)

      const normalizedWidth = (raw) => {
        const value = String(raw || '').trim()
        if (!value) return ''
        if (value.endsWith('%')) return value
        const parsed = Number.parseInt(value, 10)
        return Number.isFinite(parsed) && parsed > 0
          ? `${Math.max(120, Math.min(1200, parsed))}px`
          : ''
      }

      const applyWidth = (raw) => {
        const cssWidth = normalizedWidth(raw)
        if (cssWidth) {
          wrapper.style.width = cssWidth
          img.style.width = '100%'
          img.style.height = 'auto'
        } else {
          wrapper.style.width = ''
          img.style.width = ''
          img.style.height = 'auto'
        }
        img.style.maxWidth = '100%'
      }

      const applyAttributes = (attrs = {}) => {
        const htmlAttrs = { ...(this.options.HTMLAttributes || {}), ...attrs }
        const memoFileId = storedFileIdFromMemoImageSrc(htmlAttrs.src)
        Array.from(img.attributes).forEach((attr) => img.removeAttribute(attr.name))
        Object.entries(htmlAttrs).forEach(([key, value]) => {
          if (value == null || key === 'align') return
          if (key === 'src' && memoFileId) return
          img.setAttribute(key, String(value))
        })
        if (memoFileId) img.dataset.fileId = canonicalStoredFileIdForApiPath(memoFileId)
        const align = normalizeMemoImageAlign(attrs.align)
        if (align) {
          wrapper.dataset.align = align
        } else {
          delete wrapper.dataset.align
        }
        wrapper.style.display = align ? 'block' : 'inline-block'
        wrapper.style.marginLeft = align === 'center' || align === 'right' ? 'auto' : ''
        wrapper.style.marginRight = align === 'center' || align === 'left' ? 'auto' : ''
        applyWidth(attrs.width)
      }
      applyAttributes(node.attrs)

      const originalSrc = String(node.attrs?.src || '')
      const fileId = storedFileIdFromMemoImageSrc(originalSrc)
      let cancelled = false
      let objectUrl = ''
      let dragging = false

      const selectImageNode = () => {
        const pos = typeof getPos === 'function' ? getPos() : null
        if (typeof pos !== 'number') return
        try {
          editor.chain().focus().setNodeSelection(pos).run()
        } catch {}
      }

      wrapper.addEventListener('click', selectImageNode)

      resizeHandle.addEventListener('pointerdown', (event) => {
        if (!editor?.isEditable) return
        const startPos = typeof getPos === 'function' ? getPos() : null
        if (typeof startPos !== 'number') return
        event.preventDefault()
        event.stopPropagation()
        dragging = true
        wrapper.classList.add('is-resizing')
        selectImageNode()
        resizeHandle.setPointerCapture?.(event.pointerId)
        const startX = event.clientX
        const startWidth = wrapper.getBoundingClientRect().width || img.getBoundingClientRect().width || 240
        const parentWidth =
          wrapper.parentElement?.getBoundingClientRect().width ||
          editor.view.dom.getBoundingClientRect().width ||
          1200
        const maxWidth = Math.max(160, Math.min(1200, parentWidth - 12))
        let nextWidth = Math.round(startWidth)

        const onMove = (moveEvent) => {
          if (!dragging) return
          const delta = moveEvent.clientX - startX
          nextWidth = Math.round(Math.max(120, Math.min(maxWidth, startWidth + delta)))
          wrapper.style.width = `${nextWidth}px`
          img.style.width = '100%'
          img.setAttribute('width', String(nextWidth))
        }

        const onUp = () => {
          if (!dragging) return
          dragging = false
          wrapper.classList.remove('is-resizing')
          window.removeEventListener('pointermove', onMove)
          window.removeEventListener('pointerup', onUp)
          try {
            const pos = typeof getPos === 'function' ? getPos() : startPos
            const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
              ...currentNode.attrs,
              width: String(nextWidth),
            })
            editor.view.dispatch(tr.scrollIntoView())
          } catch {}
        }

        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
      })

      if (fileId && typeof URL !== 'undefined') {
        const pathId = canonicalStoredFileIdForApiPath(fileId)
        api.get(`/files/${encodeURIComponent(pathId)}/preview`, {
          responseType: 'blob',
          __silent: true,
        }).then((res) => {
          if (cancelled) return
          const contentType = res.headers?.['content-type'] || 'image/png'
          objectUrl = URL.createObjectURL(new Blob([res.data], { type: contentType }))
          img.setAttribute('src', objectUrl)
        }).catch(() => {})
      }

      return {
        dom: wrapper,
        update(updatedNode) {
          if (updatedNode.type !== currentNode.type || updatedNode.attrs?.src !== currentNode.attrs?.src) return false
          currentNode = updatedNode
          applyAttributes(updatedNode.attrs)
          if (objectUrl) img.setAttribute('src', objectUrl)
          return true
        },
        selectNode() {
          wrapper.classList.add('is-selected')
        },
        deselectNode() {
          wrapper.classList.remove('is-selected')
        },
        destroy() {
          cancelled = true
          if (objectUrl) {
            try { URL.revokeObjectURL(objectUrl) } catch {}
          }
        },
        ignoreMutation() {
          return true
        },
      }
    }
  },
})
function CustomerMemoAttachmentThumbnail({ att, onPreview }) {
  const fileId = att?.fileId
  const [previewUrl, setPreviewUrl] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!fileId) {
      setPreviewUrl('')
      setFailed(false)
      return undefined
    }
    let cancelled = false
    let objectUrl = ''
    setFailed(false)
    setPreviewUrl('')
    const pathId = canonicalStoredFileIdForApiPath(fileId)
    api.get(`/files/${encodeURIComponent(pathId)}/preview`, {
      responseType: 'blob',
      __silent: true,
    }).then((res) => {
      if (cancelled) return
      const contentType = res.headers?.['content-type'] || att?.contentType || 'image/png'
      objectUrl = URL.createObjectURL(new Blob([res.data], { type: contentType }))
      setPreviewUrl(objectUrl)
    }).catch(() => {
      if (!cancelled) setFailed(true)
    })
    return () => {
      cancelled = true
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl) } catch {}
      }
    }
  }, [att?.contentType, fileId])

  if (previewUrl) {
    return (
      <button
        type="button"
        className="h-12 w-12 shrink-0 overflow-hidden rounded focus:outline-none focus:ring-2 focus:ring-brand-sky"
        aria-label={`${attachmentName(att)} 미리보기`}
        onClick={() => onPreview?.(att)}
      >
        <img
          src={previewUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </button>
    )
  }
  return (
    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded text-[10px] font-bold ${
      failed ? 'bg-rose-50 text-rose-500' : 'bg-slate-100 text-slate-400'
    }`}>
      IMG
    </span>
  )
}

function fileSizeLabel(size) {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`
  return `${Math.max(1, Math.round(n / 1024))}KB`
}

function attachmentFileTypeLabel(att) {
  const contentType = String(att?.contentType || '').toLowerCase()
  if (contentType === 'application/pdf') return 'PDF'
  const name = attachmentName(att)
  const extension = name.includes('.') ? name.split('.').pop().toUpperCase() : ''
  if (!extension) return 'FILE'
  const aliases = {
    JPEG: 'JPG',
    DOCX: 'DOC',
    XLSX: 'XLS',
    PPTX: 'PPT',
  }
  return aliases[extension] || extension.slice(0, 5)
}



const CustomerMemoFile = Node.create({
  name: 'customerMemoFile',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      fileId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-file-id') || '',
        renderHTML: (attributes) => (attributes.fileId ? { 'data-file-id': attributes.fileId } : {}),
      },
      name: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-file-name') || element.textContent?.trim() || '',
        renderHTML: (attributes) => (attributes.name ? { 'data-file-name': attributes.name } : {}),
      },
      size: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-file-size') || '',
        renderHTML: (attributes) => (attributes.size ? { 'data-file-size': attributes.size } : {}),
      },
      contentType: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-content-type') || '',
        renderHTML: (attributes) => (attributes.contentType ? { 'data-content-type': attributes.contentType } : {}),
      },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-customer-memo-file]' }, { tag: 'a[data-customer-memo-file]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    const attrs = node?.attrs || {}
    const fileId = attrs.fileId || HTMLAttributes['data-file-id'] || ''
    const name = HTMLAttributes.name || HTMLAttributes['data-file-name'] || fileId || '첨부 파일'
    const size = attrs.size || HTMLAttributes['data-file-size'] || ''
    const contentType = attrs.contentType || HTMLAttributes['data-content-type'] || ''
    const className = [HTMLAttributes.class, 'customer-memo-file-chip'].filter(Boolean).join(' ')
    return ['span', {
      ...HTMLAttributes,
      class: className,
        'data-customer-memo-file': 'true',
        'data-file-id': fileId,
        'data-file-name': name,
        'data-file-size': size,
        'data-content-type': contentType,
    },
      ['span', { class: 'customer-memo-file-chip-icon' }, 'FILE'],
      ['span', { class: 'customer-memo-file-chip-text' },
        ['span', { class: 'customer-memo-file-chip-name' }, name],
        ['span', { class: 'customer-memo-file-chip-size' }, fileSizeLabel(size) || contentType || '첨부 파일'],
      ],
    ]
  },
  addNodeView() {
    return ({ node }) => {
      let currentNode = node
      const wrapper = document.createElement('span')
      wrapper.setAttribute('contenteditable', 'false')
      const icon = document.createElement('span')
      const textWrap = document.createElement('span')
      const nameEl = document.createElement('span')
      const sizeEl = document.createElement('span')
      wrapper.appendChild(icon)
      wrapper.appendChild(textWrap)
      textWrap.appendChild(nameEl)
      textWrap.appendChild(sizeEl)

      const applyFileChipAttributes = (attrs = {}) => {
        const fileId = attrs.fileId || ''
        const name = attrs.name || fileId || '첨부 파일'
        const size = attrs.size || ''
        const contentType = attrs.contentType || ''
        wrapper.className = 'customer-memo-file-chip'
        wrapper.dataset.customerMemoFile = 'true'
        wrapper.dataset.fileId = fileId
        wrapper.dataset.fileName = name
        wrapper.dataset.fileSize = size
        wrapper.dataset.contentType = contentType
        icon.className = 'customer-memo-file-chip-icon'
        icon.textContent = 'FILE'
        textWrap.className = 'customer-memo-file-chip-text'
        nameEl.className = 'customer-memo-file-chip-name'
        nameEl.textContent = name
        sizeEl.className = 'customer-memo-file-chip-size'
        sizeEl.textContent = fileSizeLabel(size) || contentType || '첨부 파일'
      }

      applyFileChipAttributes(node.attrs)

      return {
        dom: wrapper,
        update(updatedNode) {
          if (updatedNode.type !== currentNode.type) return false
          currentNode = updatedNode
          applyFileChipAttributes(updatedNode.attrs)
          return true
        },
        ignoreMutation() {
          return true
        },
      }
    }
  },
})

function insertMemoAttachmentReference(editor, att, { insertImage = false, appendToEnd = false } = {}) {
  if (!editor || !att?.fileId) return
  const chain = appendToEnd ? editor.chain().focus('end') : editor.chain().focus()
  if (attachmentIsImage(att) && insertImage) {
    chain.setImage({ src: resolveApiAssetUrl(att.fileId), alt: attachmentName(att) }).run()
    return
  }
  const name = attachmentName(att)
  const size = att.size != null ? String(att.size) : ''
  chain.insertContent([
    {
      type: 'customerMemoFile',
      attrs: {
        fileId: att.fileId,
        name,
        size,
        contentType: att.contentType || '',
      },
    },
    { type: 'text', text: ' ' },
  ]).run()
}
function pastedClipboardHasImage(event) {

  const data = event?.clipboardData
  if (!data) return false
  const items = Array.from(data.items || [])
  if (items.some((item) => item?.kind === 'file' && String(item.type || '').toLowerCase().startsWith('image/'))) return true
  return Array.from(data.files || []).some((file) => String(file?.type || '').toLowerCase().startsWith('image/'))
}

function imageExtensionFromMime(type) {
  const normalized = String(type || '').toLowerCase()
  if (normalized === 'image/png') return 'png'
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') return 'jpg'
  if (normalized === 'image/webp') return 'webp'
  if (normalized === 'image/heic') return 'heic'
  if (normalized === 'image/heif') return 'heif'
  return ''
}

function imageExtensionFromName(name) {
  const ext = String(name || '').split('.').pop()?.toLowerCase() || ''
  return ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'].includes(ext) ? ext : ''
}

function normalizePastedImageFile(file, index = 0) {
  if (!file) return null
  const ext = imageExtensionFromName(file.name) || imageExtensionFromMime(file.type)
  if (!ext) return null
  const name = String(file.name || '').trim()
  if (name && imageExtensionFromName(name)) return file
  if (typeof File === 'undefined') return file
  const safeExt = ext === 'jpeg' ? 'jpg' : ext
  const mimeType = file.type || (safeExt === 'jpg' ? 'image/jpeg' : `image/${safeExt}`)
  return new File([file], `pasted-image-${Date.now()}-${index}.${safeExt}`, { type: mimeType })
}

function pastedImageFilesFromEvent(event) {
  const data = event?.clipboardData
  if (!data) return []
  const raw = []
  Array.from(data.items || []).forEach((item) => {
    if (item?.kind === 'file' && String(item.type || '').toLowerCase().startsWith('image/')) {
      const file = item.getAsFile?.()
      if (file) raw.push(file)
    }
  })
  Array.from(data.files || []).forEach((file) => {
    if (String(file?.type || '').toLowerCase().startsWith('image/')) raw.push(file)
  })
  const seen = new Set()
  return raw
    .map((file, index) => normalizePastedImageFile(file, index))
    .filter(Boolean)
    .filter((file) => {
      const key = `${file.name || ''}:${file.size || 0}:${file.type || ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}
export function CustomerMemoLinkCards({ links = [] }) {
  const visible = Array.isArray(links) ? links.filter((item) => item?.url).slice(0, 4) : []
  if (visible.length === 0) return null
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {visible.map((item) => (
        <a
          key={item.url}
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs hover:border-brand-sky hover:bg-white"
        >
          <span className="block font-semibold text-slate-800">{item.host || '링크'}</span>
          <span className="mt-0.5 block truncate text-slate-500">{item.label || item.url}</span>
        </a>
      ))}
    </div>
  )
}

export function CustomerMemoAttachmentList({ attachments = [], disabled = false, onRemove, onReorder }) {
  const { show } = useToast()
  const rows = Array.isArray(attachments) ? attachments : []
  const handleDownload = useCallback((event, att) => {
    event.preventDefault()
    event.stopPropagation()
    if (!att?.fileId) return
    void openAuthenticatedMemoFile({ fileId: att.fileId, action: 'download', fallbackName: attachmentName(att), show })
  }, [show])
  const handlePreview = useCallback((att) => {
    void openAuthenticatedMemoFile({ fileId: att.fileId, action: 'preview', fallbackName: attachmentName(att), show })
  }, [show])
  if (rows.length === 0) return null
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="customer-memo-attachments">
      {rows.map((att, index) => {
        const id = att?.fileId || `${index}`
        const isImage = attachmentIsImage(att)
        return (
          <div key={`${id}:${index}`} data-testid="customer-memo-attachment" className="flex min-w-0 items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
            {isImage && att?.fileId ? (
              <CustomerMemoAttachmentThumbnail att={att} onPreview={handlePreview} />
            ) : (
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] font-semibold text-slate-600">{attachmentFileTypeLabel(att)}</span>
            )}
            <div className="min-w-0 flex-1">
              <a
                className="block truncate text-xs font-semibold text-slate-800 hover:underline"
                href={attachmentDownloadHref(att?.fileId)}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => handleDownload(event, att)}
              >
                {attachmentName(att)}
              </a>
              <div className="text-[11px] text-slate-500">{fileSizeLabel(att?.size)}</div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {onReorder ? (
                <>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    disabled={disabled || index === 0}
                    onClick={() => onReorder(index, -1)}
                  >
                    위
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    disabled={disabled || index >= rows.length - 1}
                    onClick={() => onReorder(index, 1)}
                  >
                    아래
                  </button>
                </>
              ) : null}
              {onRemove ? (
                <button
                  type="button"
                  data-testid="customer-memo-attachment-remove"
                  className="rounded border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  disabled={disabled}
                  onClick={() => onRemove(index)}
                >
                  삭제
                </button>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function CustomerMemoRichEditor({
  valueHtml,
  plainText,
  attachments = [],
  disabled = false,
  placeholder = '상담 메모, 링크, 참고 내용을 입력하세요',
  uploadPath = '/files/upload/customer-memo',
  allowAttachments = true,
  toolbarMode = 'compact',
  className = '',
  shellClassName = '',
  contentClassName = '',
  onChange,
  onAttachmentsChange,
}) {
  const { show } = useToast()
  const [uploading, setUploading] = useState(false)
  const [serverLimits, setServerLimits] = useState(null)
  const [listening, setListening] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const compactToolbar = toolbarMode === 'compact'
  const mobileToolbar = toolbarMode === 'mobile'
  const lastEmittedHtmlRef = useRef(null)
  const recognitionRef = useRef(null)
  const dragDepthRef = useRef(0)

  const speechRecognitionCtor = useMemo(() => {
    if (typeof window === 'undefined') return null
    return window.SpeechRecognition || window.webkitSpeechRecognition || null
  }, [])

  useEffect(() => () => {
    try { recognitionRef.current?.stop?.() } catch {}
    recognitionRef.current = null
  }, [])

  const synchronizedContent = useMemo(
    () => sanitizeMemoHtml(valueHtml || memoPlainTextToHtml(plainText)),
    [plainText, valueHtml],
  )

  useEffect(() => {
    if (!allowAttachments) return undefined
    let cancelled = false
    api.get('/customer-management/customer-memos/limits', { __silent: true })
      .then((res) => {
        if (!cancelled) setServerLimits(unwrapUpload(res) || null)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [allowAttachments])

  const configuredMaxAttachments = Number(serverLimits?.maxAttachmentsPerMemo)
  const maxAttachmentsPerMemo = Number.isFinite(configuredMaxAttachments) && configuredMaxAttachments > 0
    ? configuredMaxAttachments
    : null
  const maxImageBytes = numberLimit(serverLimits?.maxImageBytes, MAX_IMAGE_BYTES)
  const maxFileBytes = numberLimit(serverLimits?.maxFileBytes, MAX_FILE_BYTES)
  const maxTotalAttachmentBytes = numberLimit(serverLimits?.maxTotalAttachmentBytes, MAX_TOTAL_ATTACHMENT_BYTES)
  const userAttachmentBytes = Number(serverLimits?.userAttachmentBytes || 0)
  const userAttachmentQuotaBytes = Number(serverLimits?.userAttachmentQuotaBytes || 0)
  const remainingUserAttachmentBytes = Number(serverLimits?.remainingUserAttachmentBytes ?? Math.max(0, userAttachmentQuotaBytes - userAttachmentBytes))
  const showStorageQuota = allowAttachments && Number.isFinite(userAttachmentQuotaBytes) && userAttachmentQuotaBytes > 0
  const storageQuotaPercent = showStorageQuota ? Math.min(100, Math.max(0, Math.round((userAttachmentBytes / userAttachmentQuotaBytes) * 100))) : 0
  const emitChange = useCallback((ed) => {
    const html = sanitizeMemoHtml(ed.getHTML())
    lastEmittedHtmlRef.current = html
    const text = ed.getText('\n')
    const json = ed.getJSON()
    onChange?.({
      html,
      plainText: text,
      content: {
        schemaVersion: 2,
        format: 'tiptap-json-html-base64',
        htmlBase64: encodeMemoHtmlForStorage(html),
        json,
      },
    })
  }, [onChange])

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color.configure({ types: [TextStyle.name] }),
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      CustomerMemoFile,
      CustomerMemoImage.configure({ HTMLAttributes: { class: 'max-w-full h-auto rounded-md' } }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder }),
    ],
    content: synchronizedContent,
    editorProps: { attributes: { class: 'focus:outline-none' } },
    onUpdate: ({ editor: ed }) => emitChange(ed),
  })

  useEffect(() => {
    if (editor) editor.setEditable(!disabled)
  }, [disabled, editor])

  useEffect(() => {
    if (!editor) return
    const currentContent = sanitizeMemoHtml(editor.getHTML())
    if (synchronizedContent === currentContent) {
      if (lastEmittedHtmlRef.current === currentContent) {
        lastEmittedHtmlRef.current = null
      }
      return
    }
    if (lastEmittedHtmlRef.current === currentContent) return
    editor.commands.setContent(synchronizedContent || '', false)
    lastEmittedHtmlRef.current = null
  }, [editor, synchronizedContent])

  const uploadFiles = useCallback(async (fileList, { insertImages = false, appendToEnd = false } = {}) => {
    if (!editor) return
    const files = Array.from(fileList || []).filter(Boolean)
    if (files.length === 0) return
    let nextAttachments = [...attachments]
    let currentTotal = nextAttachments.reduce((sum, item) => sum + (Number(item?.size) || 0), 0)
    let uploaded = 0
    setUploading(true)
    try {
      for (const file of files) {
        if (maxAttachmentsPerMemo && nextAttachments.length >= maxAttachmentsPerMemo) {
          show(`메모 하나에는 첨부를 최대 ${maxAttachmentsPerMemo}개까지 추가할 수 있습니다.`, 'error')
          break
        }
        const isImage = String(file.type || '').toLowerCase().startsWith('image/')
        const max = isImage ? maxImageBytes : maxFileBytes
        if (file.size > max) {
          show(`첨부 용량은 ${isImage ? `이미지 ${fileSizeLabel(max)}` : `파일 ${fileSizeLabel(max)}`}까지 가능합니다.`, 'error')
          continue
        }
        if (currentTotal + file.size > maxTotalAttachmentBytes) {
          show(`메모 하나의 전체 첨부 용량은 ${fileSizeLabel(maxTotalAttachmentBytes)}까지 가능합니다.`, 'error')
          continue
        }
        const fd = new FormData()
        fd.append('file', file)
        const res = await api.post(uploadPath, fd, { headers: { 'Content-Type': 'multipart/form-data' }, __silent: true })
        const body = unwrapUpload(res)
        const att = makeAttachment(file, body, nextAttachments.length)
        if (!att.fileId) {
          show('파일 업로드에 실패했습니다.', 'error')
          continue
        }
        nextAttachments = [...nextAttachments, att].map((item, idx) => ({ ...item, sortOrder: idx }))
        currentTotal += file.size
        uploaded += 1
        insertMemoAttachmentReference(editor, att, {
          insertImage: insertImages && isImage,
          appendToEnd,
        })
      }
      if (uploaded > 0) {
        onAttachmentsChange?.(nextAttachments)
        show(uploaded === 1 ? '첨부가 추가되었습니다.' : `첨부 ${uploaded}개가 추가되었습니다.`, 'success')
      }
    } catch (err) {
      const msg = err?.response?.data?.message || '파일 업로드에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setUploading(false)
    }
  }, [attachments, editor, maxAttachmentsPerMemo, maxFileBytes, maxImageBytes, maxTotalAttachmentBytes, onAttachmentsChange, show, uploadPath])

  const uploadOne = useCallback((file, insertImage = false) => {
    if (!file) return
    uploadFiles([file], { insertImages: insertImage })
  }, [uploadFiles])
  const chooseFile = useCallback((accept, { insertImage = false, multiple = false, capture = '' } = {}) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple
    if (capture) input.setAttribute('capture', capture)
    input.onchange = () => uploadFiles(input.files, { insertImages: insertImage })
    input.click()
  }, [uploadFiles])

  const setSelectedImageWidth = useCallback((width) => {
    if (!editor || disabled) return
    editor.chain().focus().updateAttributes('image', { width }).run()
  }, [disabled, editor])

  const setSelectedImageAlign = useCallback((align) => {
    if (!editor || disabled) return
    editor.chain().focus().updateAttributes('image', { align }).run()
  }, [disabled, editor])

  const previewSelectedImage = useCallback(() => {
    if (!editor) return
    const attrs = editor.getAttributes('image') || {}
    const src = String(attrs.src || '').trim()
    if (!src) return
    const fileId = storedFileIdFromMemoImageSrc(src)
    if (fileId) {
      void openAuthenticatedMemoFile({ fileId, action: 'preview', fallbackName: attrs.alt || fileId, show })
      return
    }
    const opened = window.open(src, '_blank', 'noopener,noreferrer')
    if (!opened) show('이미지 미리보기를 열지 못했습니다.', 'error')
  }, [editor, show])


  const toggleSpeechInput = useCallback(() => {
    if (!editor || disabled) return
    if (!speechRecognitionCtor) {
      show('이 브라우저에서는 음성 입력을 사용할 수 없습니다.', 'info')
      return
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop() } catch {}
      recognitionRef.current = null
      setListening(false)
      return
    }
    const recognition = new speechRecognitionCtor()
    recognition.lang = 'ko-KR'
    recognition.interimResults = false
    recognition.continuous = true
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results || [])
        .slice(event.resultIndex || 0)
        .map((result) => result?.[0]?.transcript || '')
        .join(' ')
        .trim()
      if (!transcript) return
      const prefix = editor.getText('\n').trim() ? ' ' : ''
      editor.chain().focus().insertContent(prefix + escapeHtml(transcript)).run()
    }
    recognition.onerror = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognition.onend = () => {
      setListening(false)
      recognitionRef.current = null
    }
    recognitionRef.current = recognition
    setListening(true)
    recognition.start()
  }, [disabled, editor, show, speechRecognitionCtor])
  const handlePaste = useCallback((event) => {
    if (!pastedClipboardHasImage(event)) return
    event.preventDefault()
    event.stopPropagation()
    event.nativeEvent?.stopImmediatePropagation?.()
    if (!allowAttachments || disabled) return
    if (uploading) {
      show('이미지 업로드가 끝난 뒤 다시 붙여넣어 주세요.', 'info')
      return
    }
    const files = pastedImageFilesFromEvent(event)
    if (files.length === 0) {
      show('지원하지 않는 이미지 형식입니다. PNG, JPG, WEBP 이미지를 붙여넣어 주세요.', 'error')
      return
    }
    if (files.length > 1) {
      show('이미지는 한 번에 1개씩 붙여넣어 주세요.', 'info')
    }
    uploadOne(files[0], true)
  }, [allowAttachments, disabled, show, uploadOne, uploading])
  const dragHasFiles = useCallback((event) => Array.from(event.dataTransfer?.types || []).includes('Files'), [])

  const handleDragEnter = useCallback((event) => {
    if (!allowAttachments || disabled || !dragHasFiles(event)) return
    event.preventDefault()
    dragDepthRef.current += 1
    setDragActive(true)
  }, [allowAttachments, disabled, dragHasFiles])

  const handleDragOver = useCallback((event) => {
    if (!allowAttachments || disabled || !dragHasFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }, [allowAttachments, disabled, dragHasFiles])

  const handleDragLeave = useCallback((event) => {
    if (!allowAttachments || disabled || !dragHasFiles(event)) return
    event.preventDefault()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDragActive(false)
  }, [allowAttachments, disabled, dragHasFiles])

  const handleDrop = useCallback((event) => {
    if (!allowAttachments || disabled || !dragHasFiles(event)) return
    event.preventDefault()
    event.stopPropagation()
    event.nativeEvent?.stopImmediatePropagation?.()
    dragDepthRef.current = 0
    setDragActive(false)
    if (uploading) {
      show('업로드가 끝난 뒤 다시 추가해 주세요.', 'info')
      return
    }
    const dropTarget = event.target
    const dropOnExistingAsset = Boolean(dropTarget?.closest?.('img, [data-customer-memo-file]'))
    uploadFiles(event.dataTransfer?.files, { insertImages: true, appendToEnd: dropOnExistingAsset })
  }, [allowAttachments, disabled, dragHasFiles, show, uploadFiles, uploading])

  const handleMemoAssetClick = useCallback((event) => {
    const target = event.target
    const fileChip = target?.closest?.('[data-customer-memo-file]')
    if (fileChip && event.currentTarget?.contains(fileChip)) {
      const fileId = fileChip.getAttribute('data-file-id') || fileChip.dataset?.fileId || ''
      if (fileId) {
        event.preventDefault()
        event.stopPropagation()
        event.nativeEvent?.stopImmediatePropagation?.()
        const fallbackName = fileChip.getAttribute('data-file-name') || fileChip.textContent?.trim() || fileId
        void openAuthenticatedMemoFile({ fileId, action: 'download', fallbackName, show })
        return
      }
    }
    const anchor = target?.closest?.('a[href]')
    if (anchor && event.currentTarget?.contains(anchor)) {
      const route = memoFileRouteFromUrl(anchor.getAttribute('href') || anchor.href)
      if (route) {
        event.preventDefault()
        event.stopPropagation()
        event.nativeEvent?.stopImmediatePropagation?.()
        const fallbackName = String(anchor.textContent || '').replace(/^\[파일\]\s*/, '').trim() || route.fileId
        void openAuthenticatedMemoFile({ fileId: route.fileId, action: route.action, fallbackName, show })
      }
    }
  }, [show])
  const setLink = useCallback(() => {
    if (!editor || disabled) return
    const prev = editor.getAttributes('link').href || ''
    const url = window.prompt('링크 URL', prev || 'https://')
    if (url === null) return
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }, [disabled, editor])

  const toggleMemoBlue = useCallback(() => {
    if (!editor || disabled) return
    const chain = editor.chain().focus()
    if (editor.isActive('textStyle', { color: MEMO_BLUE })) {
      chain.unsetColor().run()
    } else {
      chain.setColor(MEMO_BLUE).run()
    }
  }, [disabled, editor])

  const insertChecklistLine = useCallback(() => {
    if (!editor || disabled) return
    editor.chain().focus().toggleTaskList().run()
  }, [disabled, editor])

  const insertMemoTable = useCallback(() => {
    if (!editor || disabled) return
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
  }, [disabled, editor])

  const removeAttachment = useCallback((index) => {
    const removed = attachments[index]
    const next = attachments.filter((_, idx) => idx !== index).map((item, idx) => ({ ...item, sortOrder: idx }))
    onAttachmentsChange?.(next)
    removeMemoImagesForFileId(editor, removed?.fileId)
  }, [attachments, editor, onAttachmentsChange])

  const reorderAttachment = useCallback((index, direction) => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= attachments.length) return
    const next = [...attachments]
    const [moved] = next.splice(index, 1)
    next.splice(nextIndex, 0, moved)
    onAttachmentsChange?.(next.map((item, idx) => ({ ...item, sortOrder: idx })))
  }, [attachments, onAttachmentsChange])

  if (!editor) {
    return <div className="system-notice-tiptap min-h-[260px] rounded-lg border border-slate-200 bg-slate-50" />
  }

  const inTable = editor.isActive('table')
  const selectedImageAttrs = editor.getAttributes('image') || {}
  const selectedImageWidth = selectedImageAttrs.width || ''
  const selectedImageAlign = normalizeMemoImageAlign(selectedImageAttrs.align)
  const simpleToolbar = toolbarMode === 'simple' || compactToolbar

  return (
    <div className={`space-y-3 ${className}`.trim()} data-testid="customer-memo-rich-editor">
      <div
        className={`system-notice-tiptap customer-memo-tiptap ${shellClassName} ${dragActive ? 'ring-2 ring-brand-sky ring-offset-2' : ''}`.trim()}
        data-testid="customer-memo-editor-shell"
        onClickCapture={handleMemoAssetClick}
        onPasteCapture={handlePaste}
        onDragEnterCapture={handleDragEnter}
        onDragOverCapture={handleDragOver}
        onDragLeaveCapture={handleDragLeave}
        onDropCapture={handleDrop}
      >
        <div className={`tiptap-toolbar flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-2 ${mobileToolbar ? 'customer-memo-mobile-toolbar' : 'flex-wrap'}`} data-testid="customer-memo-editor-toolbar">
          {!simpleToolbar ? (
            <>
              <ToolbarBtn title="실행 취소" disabled={disabled || !editor.can().chain().focus().undo().run()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={14} aria-hidden="true" /></ToolbarBtn>
              <ToolbarBtn title="다시 실행" disabled={disabled || !editor.can().chain().focus().redo().run()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={14} aria-hidden="true" /></ToolbarBtn>
            </>
          ) : null}
          {!simpleToolbar ? <ToolbarBtn title="제목" active={editor.isActive('heading', { level: 2 })} disabled={disabled} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H</ToolbarBtn> : null}
          {!compactToolbar ? <ToolbarBtn title={speechRecognitionCtor ? '음성 입력' : '음성 입력 미지원'} active={listening} disabled={disabled || !speechRecognitionCtor} onClick={toggleSpeechInput}>음성</ToolbarBtn> : null}
          <ToolbarBtn title="굵게" active={editor.isActive('bold')} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></ToolbarBtn>
          <ToolbarBtn title="기울임" active={editor.isActive('italic')} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolbarBtn>
          <ToolbarBtn title="밑줄" active={editor.isActive('underline')} disabled={disabled} onClick={() => editor.chain().focus().toggleUnderline().run()}><span className="underline">U</span></ToolbarBtn>
          {!simpleToolbar ? (
            <>
              <ToolbarBtn title="형광펜" active={editor.isActive('highlight')} disabled={disabled} onClick={() => editor.chain().focus().toggleHighlight({ color: HIGHLIGHT_COLOR }).run()}>형광</ToolbarBtn>
              <ToolbarBtn title="파란 글자" active={editor.isActive('textStyle', { color: MEMO_BLUE })} disabled={disabled} onClick={toggleMemoBlue}>색상</ToolbarBtn>
              <ToolbarBtn title="서식 지우기" disabled={disabled} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>초기화</ToolbarBtn>
              <ToolbarBtn title="글머리" active={editor.isActive('bulletList')} disabled={disabled} onClick={() => editor.chain().focus().toggleBulletList().run()}>•</ToolbarBtn>
              <ToolbarBtn title="번호 목록" active={editor.isActive('orderedList')} disabled={disabled} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</ToolbarBtn>
              <ToolbarBtn title="인용" active={editor.isActive('blockquote')} disabled={disabled} onClick={() => editor.chain().focus().toggleBlockquote().run()}>인용</ToolbarBtn>
              <ToolbarBtn title="왼쪽 정렬" active={editor.isActive({ textAlign: 'left' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('left').run()}>좌</ToolbarBtn>
              <ToolbarBtn title="가운데 정렬" active={editor.isActive({ textAlign: 'center' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('center').run()}>중</ToolbarBtn>
              <ToolbarBtn title="오른쪽 정렬" active={editor.isActive({ textAlign: 'right' })} disabled={disabled} onClick={() => editor.chain().focus().setTextAlign('right').run()}>우</ToolbarBtn>
              <ToolbarBtn title="링크" active={editor.isActive('link')} disabled={disabled} onClick={setLink}>링크</ToolbarBtn>
              <ToolbarBtn title="체크 항목" active={editor.isActive('taskList')} disabled={disabled} onClick={insertChecklistLine}><ListTodo size={14} aria-hidden="true" /></ToolbarBtn>
              <ToolbarBtn title="표 삽입" active={inTable} disabled={disabled} onClick={insertMemoTable}>표</ToolbarBtn>
            </>
          ) : null}
          {!simpleToolbar && inTable ? (
            <>
              <ToolbarBtn title="행 추가" disabled={disabled} onClick={() => editor.chain().focus().addRowAfter().run()}>행+</ToolbarBtn>
              <ToolbarBtn title="열 추가" disabled={disabled} onClick={() => editor.chain().focus().addColumnAfter().run()}>열+</ToolbarBtn>
              <ToolbarBtn title="행 삭제" disabled={disabled} onClick={() => editor.chain().focus().deleteRow().run()}>행-</ToolbarBtn>
              <ToolbarBtn title="열 삭제" disabled={disabled} onClick={() => editor.chain().focus().deleteColumn().run()}>열-</ToolbarBtn>
              <ToolbarBtn title="표 삭제" disabled={disabled} onClick={() => editor.chain().focus().deleteTable().run()}>표 삭제</ToolbarBtn>
            </>
          ) : null}
          {allowAttachments ? (
            <>
              <ToolbarBtn title="이미지 삽입" disabled={disabled || uploading} onClick={() => chooseFile('image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif', { insertImage: true, multiple: mobileToolbar })}><ImagePlus size={14} aria-hidden="true" /></ToolbarBtn>
              {mobileToolbar ? <ToolbarBtn title="카메라로 촬영" disabled={disabled || uploading} onClick={() => chooseFile('image/*', { insertImage: true, capture: 'environment' })}><Camera size={14} aria-hidden="true" /></ToolbarBtn> : null}
              <ToolbarBtn title="파일 첨부" disabled={disabled || uploading} onClick={() => chooseFile('.pdf,.txt,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.hwpx,image/*', { multiple: mobileToolbar })}><Paperclip size={14} aria-hidden="true" /></ToolbarBtn>
            </>
          ) : null}
          {!compactToolbar && editor.isActive('image') ? (
            <>
              <ToolbarBtn title="이미지 크게 보기" disabled={disabled} onClick={previewSelectedImage}><Eye size={14} aria-hidden="true" /></ToolbarBtn>
              <ToolbarBtn title="이미지 왼쪽 정렬" active={selectedImageAlign === 'left'} disabled={disabled} onClick={() => setSelectedImageAlign('left')}>왼쪽</ToolbarBtn>
              <ToolbarBtn title="이미지 가운데 정렬" active={selectedImageAlign === 'center'} disabled={disabled} onClick={() => setSelectedImageAlign('center')}>가운데</ToolbarBtn>
              <ToolbarBtn title="이미지 오른쪽 정렬" active={selectedImageAlign === 'right'} disabled={disabled} onClick={() => setSelectedImageAlign('right')}>오른쪽</ToolbarBtn>
              <ToolbarBtn title="이미지 정렬 해제" active={!selectedImageAlign} disabled={disabled} onClick={() => setSelectedImageAlign(null)}>해제</ToolbarBtn>
              <ToolbarBtn title="이미지 작게" active={String(selectedImageWidth) === '240'} disabled={disabled} onClick={() => setSelectedImageWidth('240')}>S</ToolbarBtn>
              <ToolbarBtn title="이미지 보통" active={String(selectedImageWidth) === '420'} disabled={disabled} onClick={() => setSelectedImageWidth('420')}>M</ToolbarBtn>
              <ToolbarBtn title="이미지 전체" active={String(selectedImageWidth) === '100%'} disabled={disabled} onClick={() => setSelectedImageWidth('100%')}>100%</ToolbarBtn>
            </>
          ) : null}
        </div>
        <BubbleMenu
          editor={editor}
          options={{ placement: 'top' }}
          shouldShow={({ editor: activeEditor, state }) => Boolean(activeEditor?.isEditable) && !state.selection.empty && !activeEditor.isActive('image')}
        >
          <div className="customer-memo-bubble-menu" onMouseDown={(event) => event.preventDefault()}>
            <ToolbarBtn title="굵게" active={editor.isActive('bold')} disabled={disabled} onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></ToolbarBtn>
            <ToolbarBtn title="기울임" active={editor.isActive('italic')} disabled={disabled} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></ToolbarBtn>
            <ToolbarBtn title="밑줄" active={editor.isActive('underline')} disabled={disabled} onClick={() => editor.chain().focus().toggleUnderline().run()}><span className="underline">U</span></ToolbarBtn>
            {!compactToolbar ? <>
              <ToolbarBtn title="형광펜" active={editor.isActive('highlight')} disabled={disabled} onClick={() => editor.chain().focus().toggleHighlight({ color: HIGHLIGHT_COLOR }).run()}>형광</ToolbarBtn>
              <ToolbarBtn title="파란 글자" active={editor.isActive('textStyle', { color: MEMO_BLUE })} disabled={disabled} onClick={toggleMemoBlue}>파랑</ToolbarBtn>
              <ToolbarBtn title="링크" active={editor.isActive('link')} disabled={disabled} onClick={setLink}>링크</ToolbarBtn>
            </> : null}
          </div>
        </BubbleMenu>
        <div data-testid="customer-memo-editor-content" className={contentClassName}><EditorContent editor={editor} /></div>
      </div>
      {showStorageQuota ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-700">첨부 저장공간</span>
            <span>{fileSizeLabel(userAttachmentBytes)} / {fileSizeLabel(userAttachmentQuotaBytes)} · 남은 용량 {fileSizeLabel(remainingUserAttachmentBytes)}</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
            <div className={`h-full rounded-full ${storageQuotaPercent >= 90 ? 'bg-rose-500' : storageQuotaPercent >= 70 ? 'bg-amber-500' : 'bg-brand-sky'}`} style={{ width: `${storageQuotaPercent}%` }} />
          </div>
        </div>
      ) : null}
      {allowAttachments ? <CustomerMemoAttachmentList attachments={attachments} disabled={disabled || uploading} onRemove={removeAttachment} onReorder={reorderAttachment} /> : null}
    </div>
  )
}
